/**
 * The continuation record store, and the pure derivation the door's state
 * machine runs on.
 *
 * After a visitor asks for a magic link, the 202 hands this device a
 * `{flow_id, request_secret}` pair. That pair is the device's capability to ask
 * "has this been approved yet?" and, once it has, to claim the one-time token
 * and sign in HERE rather than in the tab that opened the mail. This module is
 * where those pairs live: up to `MAX_CONTINUATIONS` of them, newest-first, each
 * pruned at `AUTH_FLOW_TTL_MS`.
 *
 * WHY A LIST AND NOT ONE RECORD. A visitor who asks twice (a typo, a resend, a
 * second address) has two live flows, and the one they approve is not
 * necessarily the newest. `deriveDoorState` is scoped to the newest record for
 * every terminal state — those describe what the visitor is looking at — but
 * `approved` is deliberately the one cross-record winner, so approving the
 * first link after requesting a second still signs them in. Unless the newest
 * was DENIED: a deny ends the whole wait (#1163, see `deriveDoorState`).
 *
 * WHAT THAT WINNER IS SCOPED BY, and it is not this module. The STORED list is
 * every pair this browser profile holds, because `/auth/link` needs to find one
 * it did not write; the list `deriveDoorState` is handed is the door's own,
 * which `AuthDoor.tsx` keeps as the flows THIS page life is still waiting on
 * (see `stopWaitingOnCurrentFlow` there, which both clears the door's `records`
 * state and forgets the same ids from storage on a new send or on "Wrong
 * address?"). Both halves matter: without the second one, a pair left over from
 * an earlier attempt in this same page life won the cross-record `approved` and
 * the door claimed a flow nobody on this screen had asked for any more, and a
 * flow the door had already finished with could be rewritten into a verdict
 * about the new one. Handing this function a raw, unscoped `readContinuations()`
 * result is that bug.
 *
 * WHY EVERY EXPORT IS ASYNC. The web adapter is synchronous `localStorage`
 * behind `@utils/magicLinkMirror`, but the native adapter is Capacitor
 * Preferences, which is not. One async surface means the door, the poller and
 * plan 6's native leg all call the same functions; nothing branches on platform
 * above this file.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: `queue` below is the ONE
 * module-level mutable value in this feature, and it is deliberate. The plan's
 * "no module-level mutable state" constraint is scoped to hooks and components,
 * where module state survives unmount and leaks between tests; this is storage
 * plumbing with no React lifetime at all. It exists because all four exported
 * operations are read-modify-writes over an ASYNC store, so two issued in the
 * same tick both read the pre-write list and the second write silently discards
 * the first. The door does exactly that: a `taken` status fires
 * `forgetContinuationUnlessClaimed` while a fresh 202 fires
 * `rememberContinuation`. Chaining
 * every operation through one promise (`queue.then(op, op)` — the second `op`
 * is the rejection arm, so one failure cannot poison the chain) makes them run
 * in issue order. Nothing here is re-entrant, so the chain cannot deadlock.
 *
 * Nothing in this module logs, tracks or otherwise emits `requestSecret` or
 * `flowId`. `flowHash` exists so analytics can correlate a flow without
 * carrying either.
 */
import { AUTH_FLOW_TTL_MS } from '@utils/authFlowTtl';
import {
  MAGIC_LINK_MIRROR_KEYS,
  readMagicLinkMirror,
  writeMagicLinkMirror,
} from '@utils/magicLinkMirror';
import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';
import { featureFlags } from '@config/featureFlags';
import type { ContinuationStatus } from '@models/api/auth';

/** One pending continuation: the pair the 202 handed back, plus when it arrived. */
export interface ContinuationRecord {
  flowId: string;
  requestSecret: string;
  createdAt: number;
  /**
   * When this device first learned the flow was CLAIMED: the claiming tab just
   * before it redeems, a door that hears a sibling's broadcast, a door whose
   * poll answers `claimed`, or the provider's check answering the same.
   * Absent on every record that has not been claimed, and on every record
   * written before this field existed.
   *
   * AIDEV-NOTE [MEETBALL magic-link continuation]: #1161. `claimed` proves a
   * spent token, not a session. The claim stores the minted token server side
   * and re-serves it to the same request secret for the requester window, so
   * this record is the way back to it if the redemption fails. A marked record
   * is therefore KEPT (see `isClaimRetained` and
   * `forgetContinuationUnlessClaimed`) and NEVER SENT: its secret is spent, so
   * no status body carries it and no seed puts it back under a poll.
   */
  claimedAt?: number;
  /**
   * When a door that did NOT send this flow was told by the visitor that it is
   * not theirs: "Wrong address?" over it, or a new send that replaces it
   * (marked at the click, so a send that then fails does not undo it).
   * Absent on every other record, and on every record written before this
   * field existed.
   *
   * AIDEV-NOTE [MEETBALL magic-link continuation]: C-1 of review 5330766393.
   * A door deletes the records it SENT when the visitor turns them down, but
   * one it ADOPTED belongs to whoever sent it (round 1's F2: another tab may
   * still be waiting on it), so it used to stay in storage untouched, and the
   * app-wide provider and the next cold read then resumed it like any other.
   * On an approval that signed the visitor in as the address they had just
   * rejected. The mark records the rejection where every reader that can
   * start a sign-in looks: the provider leaves a marked record out of its
   * check, and the door's cold read and router-state entry never seed one.
   * A tab already waiting on the flow keeps its own in-memory copy, which is
   * what F2 protects; what it gives up is re-adopting the flow after a reload.
   */
  abandonedAt?: number;
}

/**
 * The cap on stored records. Five is generous for "asked more than once" and
 * bounds both the status POST body and what a compromised profile can replay.
 */
export const MAX_CONTINUATIONS = 5;

/**
 * How long a claimed record is kept after `claimedAt`: the backend's requester
 * re-read window plus a margin.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: #1161. The window is the
 * backend's `MAGIC_LINK_REQUESTER_TOKEN_SECONDS` (`app/settings.py`, 300s):
 * the minted token lives that long from CLAIM_COMMIT and is re-servable to the
 * same secret for `MAGIC_LINK_CLAIM_READS` reads inside it. Every writer of the
 * marker writes it AFTER the commit (the claim's own response, or a later
 * observation of it), and both ends of the comparison are this device's clock,
 * so the window alone already covers the backend's. The margin is for that
 * clock stepping forward between the mark and the check, which would otherwise
 * end the window early. Named apart from the door's `CLAIM_WINDOW_MS`, which is
 * the claim RETRY's wall clock and a different thing.
 *
 * A number in two repositories: `continuation.test.ts` pins it against a
 * hand-copied literal of the backend setting. Move both together.
 */
const REQUESTER_TOKEN_WINDOW_MS = 300_000;
const CLAIMED_RECORD_MARGIN_MS = 60_000;
export const CLAIMED_RECORD_RETENTION_MS = REQUESTER_TOKEN_WINDOW_MS + CLAIMED_RECORD_MARGIN_MS;

/** A record some party has marked claimed: its secret never goes on the wire again. */
export const isMarkedClaimed = (record: ContinuationRecord): boolean =>
  record.claimedAt !== undefined;

/**
 * A marked record whose retention has not run out, which no forget but the
 * session-proven clear, sign-out, the account switch and the TTL may delete.
 *
 * ONE-SIDED, the opposite of `liveContinuations`' two-sided TTL, and on
 * purpose. There a record dated ahead of the clock would otherwise be live
 * forever; here keeping a record too long is still bounded by that TTL, while
 * forgetting it too early deletes the way back to a token the backend is still
 * serving. So a marker dated ahead of the clock keeps its record.
 */
export const isClaimRetained = (record: ContinuationRecord, now: number): boolean =>
  record.claimedAt !== undefined && now - record.claimedAt < CLAIMED_RECORD_RETENTION_MS;

const KEY = MAGIC_LINK_MIRROR_KEYS.continuation;

const isRecord = (value: unknown): value is ContinuationRecord => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<ContinuationRecord>;
  return (
    typeof candidate.flowId === 'string' &&
    candidate.flowId.length > 0 &&
    typeof candidate.requestSecret === 'string' &&
    candidate.requestSecret.length > 0 &&
    typeof candidate.createdAt === 'number' &&
    Number.isFinite(candidate.createdAt) &&
    // OPTIONAL, because every record written before the marker existed has
    // none; but present means a finite number, since the marker decides what
    // goes on the wire and what may be deleted.
    (candidate.claimedAt === undefined ||
      (typeof candidate.claimedAt === 'number' && Number.isFinite(candidate.claimedAt))) &&
    // OPTIONAL, because every record written before the mark existed has
    // none; but present means a finite number, since the mark decides which
    // readers may start a sign-in from the record.
    (candidate.abandonedAt === undefined ||
      (typeof candidate.abandonedAt === 'number' && Number.isFinite(candidate.abandonedAt)))
  );
};

/**
 * A record a door has marked abandoned: no reader that can start a sign-in
 * (the provider's check, the door's cold read, its router-state entry) may
 * act on it. See `ContinuationRecord.abandonedAt`.
 */
export const isAbandoned = (record: ContinuationRecord): boolean =>
  record.abandonedAt !== undefined;

/**
 * Whole-list validation: one bad entry rejects the list rather than being
 * filtered out of it. A payload we only half understand is a payload written by
 * something other than this module, and half-trusting it is how a malformed
 * record reaches the claim call.
 */
const isRecordList = (value: unknown): value is ContinuationRecord[] =>
  /*
   * AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074232258. LENGTH is
   * part of the shape. The backend's status route caps a batch at
   * `_MAX_STATUS_ENTRIES = 5` and answers 400 above it, and `MAX_CONTINUATIONS`
   * is the client half of that same number -- so an over-long list is not a
   * list this module could have written, and accepting one POSTs it whole and
   * takes a permanent 400 (which the poll reads as a contract break and stops
   * on) for the rest of the page life. No in-repo writer produces one today:
   * every growth path slices, including the replace branch below since this
   * round. That is exactly why the bound belongs on the READ -- the only way in
   * is a foreign same-origin write or a future raised cap, and neither will
   * remember to come back here.
   */
  Array.isArray(value) && value.length <= MAX_CONTINUATIONS && value.every(isRecord);

// ---------------------------------------------------------------------------
// Storage adapters
// ---------------------------------------------------------------------------

/**
 * AIDEV-NOTE [MEETBALL magic-link continuation]: the two adapters store
 * slightly different bytes under the same key, and that asymmetry is intended.
 * Web goes through `magicLinkMirror`'s envelope, so it inherits the shared
 * `writtenAt` TTL and the reject-and-remove behaviour every other mirror has.
 * Native stores the bare JSON list, because Preferences has no envelope
 * convention here and the per-record `createdAt` pruning below is the
 * authoritative clock on both platforms anyway. Nothing reads one platform's
 * bytes on the other.
 */
const readStored = async (): Promise<ContinuationRecord[]> => {
  if (isNativePlatform()) {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: KEY });
      if (!value) {
        return [];
      }
      const parsed: unknown = JSON.parse(value);
      return isRecordList(parsed) ? parsed : [];
    } catch (error) {
      logger.warn('[continuation] Failed to read native Preferences:', error);
      return [];
    }
  }

  return readMagicLinkMirror(KEY, isRecordList) ?? [];
};

const writeStored = async (records: ContinuationRecord[]): Promise<void> => {
  if (isNativePlatform()) {
    try {
      const { Preferences } = await import('@capacitor/preferences');
      if (records.length === 0) {
        await Preferences.remove({ key: KEY });
        return;
      }
      await Preferences.set({ key: KEY, value: JSON.stringify(records) });
    } catch (error) {
      logger.warn('[continuation] Failed to write native Preferences:', error);
    }
    return;
  }

  // `null` REMOVES the mirror (see `writeMagicLinkMirror`), which is what an
  // empty list means: there is nothing pending, so leave nothing behind.
  writeMagicLinkMirror(KEY, records.length === 0 ? null : records);
};

// ---------------------------------------------------------------------------
// Serialisation
// ---------------------------------------------------------------------------

let queue: Promise<unknown> = Promise.resolve();

const serialised = <T>(op: () => Promise<T>): Promise<T> => {
  // `op` is passed as BOTH arms: a previous operation that rejected must not
  // stop the next one from running, and the chain must not stay rejected.
  const next = queue.then(op, op);
  queue = next;
  return next;
};

/**
 * Every record still inside the TTL, newest-first.
 *
 * Exported because the poll's request BODY has to be built from the same view
 * its own liveness gate and `deriveDoorState` use — see
 * `useContinuationStatus.ts`'s `queryFn`, which used to map the raw list
 * instead, so a record one millisecond past the TTL kept riding along in every
 * later poll and spent its `request_secret` on a flow the backend had already
 * dropped.
 */
export const liveContinuations = (
  records: ContinuationRecord[],
  now: number,
): ContinuationRecord[] =>
  /*
   * AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074243150. The window is
   * TWO-SIDED. `now - createdAt < TTL` is true for every record dated AHEAD of
   * the clock, and `isRecord` bounds `createdAt` by `Number.isFinite` alone, so
   * a future-dated record was unconditionally live -- for the whole skew plus
   * the 30 minutes -- and un-prunable, since `readContinuations` prunes through
   * this same predicate. It was also the record that DESCRIBED THE SCREEN:
   * `newestLive` and `deriveDoorState` both take the head of this list, which
   * a far-future `createdAt` sorts nothing but is first in insertion order at
   * the head. Reachable without an attacker: the web copy is same-origin
   * writable, and a device whose clock is corrected backwards dates its own
   * records ahead of the new `Date.now()`.
   *
   * `Math.abs` and not a `createdAt <= now` clamp, because a small forward skew
   * between the write and the read is ordinary and must not expire a live pair.
   * The boundary is spelled `< TTL` on this side and `>= TTL` in the two
   * envelope readers, which is the same instant from either direction; see
   * @utils/authFlowTtl.
   */
  records.filter(candidate => Math.abs(now - candidate.createdAt) < AUTH_FLOW_TTL_MS);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * The newest record still inside the TTL, or `undefined`.
 *
 * Exported because the door needs the same answer `deriveDoorState` works from
 * — the record whose status the poll is really about — and two copies of the
 * same filter is how the two drift.
 */
export const newestLive = (
  records: ContinuationRecord[],
  now: number,
): ContinuationRecord | undefined => liveContinuations(records, now)[0];

/** Every live record, newest-first. `[]` on any storage failure. */
export const readContinuations = async (now = Date.now()): Promise<ContinuationRecord[]> =>
  serialised(async () => {
    const stored = await readStored();
    const live = liveContinuations(stored, now);
    // Pruning on read is also a WRITE: an expired capability that nothing ever
    // rewrites would sit in the profile for as long as the browser lives.
    if (live.length !== stored.length) {
      await writeStored(live);
    }
    return live;
  });

/**
 * `list` (newest-first, its head the pair just added) with ONE record removed:
 * the oldest record not RETAINED as claimed (`isClaimRetained`), or the oldest
 * of all when every record behind the head is. The head itself is never the
 * one to go, because it is the flow the visitor has just asked for.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: #1161. A marked record is kept
 * for `CLAIMED_RECORD_RETENTION_MS` and still counts against the five, so a
 * plain oldest-first slice evicted a record another tab may be redeeming on
 * after five newer sends inside its window. The list still ends at five:
 * `isRecordList` rejects a longer one whole.
 *
 * RETAINED, not merely marked (finding F4 of PR #1209's adversarial review).
 * Past its window a marked record has no way back left to keep: the backend
 * no longer re-serves its token, and every guarded forget already deletes it.
 * Protecting it there evicted a live UNMARKED record in its place, possibly a
 * pending link another tab is waiting on, and on `/auth`, where the provider's
 * check (the one sweep that forgets a claim past its window) does not run, a
 * spent record could outrank it for the rest of the TTL.
 */
const evictOne = (list: ContinuationRecord[], now: number): ContinuationRecord[] => {
  let victim = list.length - 1;
  for (let index = list.length - 1; index > 0; index -= 1) {
    if (!isClaimRetained(list[index], now)) {
      victim = index;
      break;
    }
  }
  return list.filter((_record, index) => index !== victim);
};

/**
 * Records a 202's pair and resolves to the new list.
 *
 * Resolving to the list rather than to `void` is what lets the door set its
 * state from the same value it just wrote, instead of issuing a read that races
 * the next mutation.
 *
 * A `flow_id` already on file is REPLACED IN PLACE, keeping its original
 * `createdAt`: the backend may re-deliver a pair for a flow already pending
 * (a retried 202, a resend that resolves to the same flow), and treating that
 * as a new record would both duplicate the entry and reset a clock the backend
 * never reset.
 *
 * When storage is unusable — Safari with "Block All Cookies" throws on the
 * `localStorage` access itself — this still resolves to the computed list
 * rather than to `[]`. The write is lost, so a reload forgets the flow, but the
 * door holds the pair in its own state and can poll and claim on it for the
 * life of this page. Degrading to `[]` here would turn a storage failure into
 * "we never asked", which is the worse of the two.
 */
export const rememberContinuation = async (
  pair: { flow_id: string; request_secret: string },
  now = Date.now(),
): Promise<ContinuationRecord[]> =>
  serialised(async () => {
    // Clamped ONCE, before either branch, so both write a list the backend's
    // batch cap can accept (id 4074232258). The replace branch used to skip the
    // clamp entirely -- it cannot grow the list, so it read as safe -- which
    // left an over-long list read from storage written straight back out at
    // full length on the next resend of a pair already in it. `isRecordList`
    // rejects such a list on the way in now, so this is the belt to that brace;
    // clamping first also keeps `existing` an index into the list actually
    // being written rather than into a longer one.
    const live = liveContinuations(await readStored(), now).slice(0, MAX_CONTINUATIONS);
    const existing = live.findIndex(candidate => candidate.flowId === pair.flow_id);

    let next: ContinuationRecord[];
    if (existing === -1) {
      next = [
        { flowId: pair.flow_id, requestSecret: pair.request_secret, createdAt: now },
        ...live,
      ];
      if (next.length > MAX_CONTINUATIONS) {
        next = evictOne(next, now);
      }
    } else {
      next = [...live];
      next[existing] = { ...next[existing], requestSecret: pair.request_secret };
    }

    await writeStored(next);
    return next;
  });

/**
 * Stamps `abandonedAt` on one stored record and resolves to the list.
 *
 * FIRST MARK WINS, and a flow that is not stored is left alone rather than
 * created: the mark describes a record, and there is none here to keep from
 * anybody. The record itself stays, for its owner and the TTL (F2); see
 * `ContinuationRecord.abandonedAt`.
 */
export const markContinuationAbandoned = async (
  flowId: string,
  now = Date.now(),
): Promise<ContinuationRecord[]> =>
  serialised(async () => {
    const live = liveContinuations(await readStored(), now);
    const index = live.findIndex(candidate => candidate.flowId === flowId);
    if (index === -1 || isAbandoned(live[index])) {
      return live;
    }
    const next = [...live];
    next[index] = { ...next[index], abandonedAt: now };
    await writeStored(next);
    return next;
  });

/**
 * Stamps `claimedAt` on one stored record and resolves to the list.
 *
 * FIRST LEARNED WINS: a record that already carries a marker keeps it, so a
 * second writer (a sibling that heard the broadcast, a poll that answered
 * `claimed`) cannot lengthen the window. A flow that is not stored is left
 * alone rather than created: the marker describes a record, and a lost write
 * means there is none here to keep.
 */
export const markContinuationClaimed = async (
  flowId: string,
  now = Date.now(),
): Promise<ContinuationRecord[]> =>
  serialised(async () => {
    const live = liveContinuations(await readStored(), now);
    const index = live.findIndex(candidate => candidate.flowId === flowId);
    if (index === -1 || isMarkedClaimed(live[index])) {
      return live;
    }
    const next = [...live];
    next[index] = { ...next[index], claimedAt: now };
    await writeStored(next);
    return next;
  });

const forgetUnlessKept = (
  flowId: string,
  now: number,
  isKept: (record: ContinuationRecord) => boolean,
): Promise<ContinuationRecord[]> =>
  serialised(async () => {
    const next = liveContinuations(await readStored(), now).filter(
      candidate => candidate.flowId !== flowId || isKept(candidate),
    );
    await writeStored(next);
    return next;
  });

/**
 * Drops one record, marked or not, and resolves to the rest.
 *
 * UNCONDITIONAL because the claiming document's session-proven clear needs it
 * to be: a session there is the proof the token was spent, and that clear must
 * not wait out the window. Every other forget that cannot prove the redemption
 * finished belongs on `forgetContinuationUnlessClaimed`, and that clear is now
 * this one's only caller: `/auth/link`'s reservation, the door's
 * `taken`/`denied` verdict and that verdict's two sweeps all moved across
 * (#1161).
 *
 * Takes `now` like the other writers here (`readContinuations`, which prunes
 * with a write, `rememberContinuation`, both marks, the guarded forget) rather
 * than reading the clock internally: it prunes on the same read-modify-write
 * they do, so a caller that pins the clock for the others and not for this one
 * gets a list pruned against two different epochs.
 */
export const forgetContinuation = async (
  flowId: string,
  now = Date.now(),
): Promise<ContinuationRecord[]> => forgetUnlessKept(flowId, now, () => false);

/**
 * Drops one record UNLESS it is marked claimed and still inside its retention
 * window (`isClaimRetained`), and resolves to the rest.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: #1161. The forget sites that
 * speak for no redemption: "Wrong address?", the send's supersession, the
 * provider's check, `/auth/link`'s reservation, and the door's `taken`/`denied`
 * marking effect with its two sweeps over `consumedRef`. The last three are a
 * live path since #1163: a deny forgets every held record the door sent, and
 * one this door knows is being redeemed is marked first and kept here. For the
 * flow a verdict is about they are defence in depth: against the backend, a
 * `taken` or `denied` the door can see implies no re-readable token.
 *
 * The marker is read from STORAGE inside the read-modify-write rather than off
 * the caller's own copy of the record, because the party that marked it may be
 * another tab: a forget issued after that tab's mark has reached this one's
 * view of storage keeps the record, broadcast or no broadcast.
 *
 * WHAT IT DOES NOT GUARANTEE. `serialised` orders operations within ONE tab
 * (one JavaScript realm) only. Nothing orders one tab's read-modify-write
 * against another's, so a forget in one tab can race a mark in another and
 * lose the record, and a mark can race another tab's write and lose the marker
 * (both shapes measured in review, two module instances over one store). A
 * known residual, not fixed: rare, and the redundant writers (the claiming tab,
 * the sibling's subscriber, the poll's `claimed`, the provider) usually
 * re-stamp a lost marker.
 */
export const forgetContinuationUnlessClaimed = async (
  flowId: string,
  now = Date.now(),
): Promise<ContinuationRecord[]> =>
  forgetUnlessKept(flowId, now, candidate => isClaimRetained(candidate, now));

/**
 * Drops every record. Sign-out only: a per-flow terminal state uses the
 * per-flow forgets above, because the visitor's other pending link is still
 * theirs to approve.
 */
export const clearContinuations = async (): Promise<void> =>
  serialised(async () => {
    await writeStored([]);
  });

/**
 * Both exit seams below are gated on `magicLinkContinuation`, and the reason is
 * in one place because it is an argument rather than a convention.
 *
 * A FLAG-OFF BUILD CANNOT HAVE WRITTEN A RECORD. `rememberContinuation` has
 * exactly two callers, both in `AuthDoor.tsx` -- the 202 effect, which returns
 * on `!featureFlags.magicLinkContinuation` before it touches anything
 * (`:787`), and the throttled-repeat re-seed, which is gated on
 * `lastAcceptedPairRef`, a ref only that same effect ever sets. So with the
 * flag off there is nothing on this device for either seam to clear, and on
 * NATIVE the seams are not free: `writeStored` is an
 * `await import('@capacitor/preferences')` plus a bridge round trip, on paths
 * (`clearOutgoingAccountStorage`, `LogoutCallback`) that are shipped and
 * reachable today.
 *
 * THE ROLLBACK CASE is the one this leaves, and it is inert. A device that
 * banked a record under a flag-ON build and then takes a flag-OFF one keeps it
 * until its own TTL. Nothing can use it: every reader is behind the same flag
 * (`AuthDoor.tsx:935`, and `decideLanding` answers `legacy` outright), every
 * read filters through `liveContinuations`, and the backend's transition routes
 * are behind `MAGIC_LINK_CONTINUATION_ENABLED` besides. Thirty minutes of a
 * capability no code path on the device will pick up, self-healing, against a
 * bridge round trip on every account switch and every sign-out for everybody
 * else -- and the flag-off build is the one every deployed environment runs
 * today.
 */
const isContinuationStorageInUse = (): boolean => featureFlags.magicLinkContinuation;

/**
 * The sign-out seam. `clearNonLogtoStorage()` / `clearUserStorage()`
 * (`@utils/storageCleanup`) are synchronous and stay that way -- they run the
 * localStorage sweep that already carries this module's web mirror by
 * construction (see the ACCEPTED RESIDUAL note below) -- so this is called
 * BESIDE them, not from inside them. The primary call sits on
 * `useSignInOut.ts`'s MAIN sign-out path, beside `clearNonLogtoStorage()`:
 * that is the same seam `clearAuthIntent()` (`@utils/authIntent.ts`) already
 * uses to clear its own native mirror, and it has to be the main path and not
 * only the `catch` fallback, because `logtoSignOut` *resolves* normally on
 * native (it only reaches the SDK-throws fallback on web, or on a native
 * failure) -- a call placed solely in that catch branch would never run on an
 * ordinary native sign-out. `LogoutCallback.tsx`'s legacy route calls it the
 * same way, beside its own `clearUserStorage()`. It exists at all only for the
 * native leg: neither sweep is a `localStorage` walk that ever reaches
 * Capacitor Preferences, so nothing else clears a native continuation record
 * on sign-out.
 *
 * Wrapped in its own try/catch for the same reason every other step in
 * `executeSignOut` is: one cleanup step's failure must not stop sign-out or
 * throw out of the caller. In practice `clearContinuations` cannot reject --
 * both storage adapters swallow their own errors -- so this is a second layer
 * against a future change to that contract, not a live path today.
 *
 * ACCEPTED RESIDUAL: the native adapter stores a bare JSON list under
 * `Preferences`, with no envelope and no `writtenAt` (see the adapter note
 * above `readStored`). If a `Preferences.set({ ... })` write during this clear
 * fails silently -- `writeStored`'s own catch logs and returns rather than
 * propagating -- the visitor's old record(s) are left behind on that device.
 * That is not a data leak: every read still runs through `liveContinuations`, so a
 * left-behind record is only ever live until its own `createdAt + AUTH_FLOW_TTL_MS`
 * and is unusable once the backend has expired the matching flow. Nothing
 * currently retries or verifies the native write; a signed-out device
 * self-heals on its own TTL rather than on a confirmed clear.
 */
export const clearContinuationOnSignOut = async (): Promise<void> => {
  if (!isContinuationStorageInUse()) {
    return;
  }
  try {
    await clearContinuations();
  } catch (error) {
    logger.warn('[continuation] Failed to clear continuations during sign-out:', error);
  }
};

/**
 * The ACCOUNT-SWITCH seam, for `clearOutgoingAccountStorage`
 * (`@utils/storageCleanup`). Same clear as the sign-out seam above, named for
 * its own caller because an account switch is not a sign-out: that screen
 * deliberately never calls `signOut()`, so none of the sign-out reasoning
 * above applies to it and a shared name would invite a reader to assume it
 * does.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: this exists because the
 * switch sweep was WEB-ONLY and the exclusion that makes it work is a
 * localStorage fact. `storageCleanup.ts` leaves `MAGIC_LINK_MIRROR_KEYS.continuation`
 * out of `AUTH_HANDOVER_KEYS` precisely so the sweep destroys it -- but
 * `clearOutgoingAccountStorage` is two synchronous `globalThis.localStorage` /
 * `globalThis.sessionStorage` walks and there is no native branch anywhere in
 * that file, while on native these records live in Capacitor Preferences (see
 * the adapters above). So on native the OUTGOING account's `{flowId,
 * requestSecret}` pairs survived the switch and lived to their TTL, where the
 * door's "WHAT IS ADOPTED" behaviour picks up every unexpired record the
 * profile holds. A continuation record is a BEARER capability with no address
 * in it, so none of `/auth/link`'s identity gates are on that path: that is a
 * way back into the account the visitor just handed the profile away from.
 * The two sign-out seams each got a hand-placed call for exactly this reason;
 * this third exit did not, and this PR is what created the storage.
 *
 * Called as `void` from inside the sweep, which is the shape `clearAuthIntent()`
 * (`@utils/authIntent.ts`) already uses for its own native mirror -- so the
 * "the sweeps are synchronous and stay that way" constraint above still holds.
 * On web it is redundant with the localStorage walk and idempotent, which is
 * why it is not gated on `isNativePlatform()` here: the adapter already makes
 * that decision once, and a second copy of it is a second thing to keep in step.
 */
export const clearContinuationOnAccountSwitch = async (): Promise<void> => {
  if (!isContinuationStorageInUse()) {
    return;
  }
  try {
    await clearContinuations();
  } catch (error) {
    logger.warn('[continuation] Failed to clear continuations during account switch:', error);
  }
};

/**
 * The first 16 hex characters of SHA-256(flowId), for analytics.
 *
 * `''` when `crypto.subtle` is unavailable (it is secure-context-only, so an
 * http origin or an old WebView has none) or when digesting throws. An absent
 * hash is a missing analytics dimension; a thrown one would take down the
 * door's effect, so this never rejects. The truncation is deliberate: the
 * dimension only has to correlate events within one flow, and a full digest is
 * a longer-lived identifier than that needs.
 */
export const flowHash = async (flowId: string): Promise<string> => {
  try {
    // Widened deliberately: TypeScript's lib types declare `crypto` as always
    // present and it is not. `subtle` is secure-context-only on top of that, so
    // an http origin or an old WebView can have the object without the
    // property — hence the guard below rather than a plain call.
    const webCrypto = globalThis.crypto as Crypto | undefined;
    const subtle = webCrypto?.subtle;
    if (!subtle) {
      return '';
    }
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(flowId));
    return [...new Uint8Array(digest)]
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch {
    return '';
  }
};

// ---------------------------------------------------------------------------
// Door state
// ---------------------------------------------------------------------------

/**
 * Everything the waiting door can be showing. One discriminated union, so the
 * screen switches once rather than reading five booleans that can disagree.
 *
 * `claiming`, `claimFailed` and `claimedElsewhere` are produced ONLY by the
 * door — by the claim call's `ok` branch, by its failure branches, and by a
 * sibling tab's `MAGIC_LINK_CLAIMED` broadcast respectively — and
 * `deriveDoorState` never returns any of them. They live here because they are
 * states of the same machine and the door's override carries them.
 * `handoffUnavailable` is a fourth of the same kind: the door's poll
 * precedence is the only thing that can produce it.
 */
export type DoorState =
  | { kind: 'unavailable' }
  /**
   * This device holds a pair and CANNOT be signed in by a link opened
   * elsewhere: the poll answered with a verdict about the pairs themselves, or
   * it never answered at all inside the door's ceiling.
   *
   * AIDEV-NOTE [MEETBALL magic-link continuation]: split out of `unavailable`,
   * and the split is the fix rather than tidying. `unavailable` covers two
   * situations that look identical in a state machine and are opposite to a
   * visitor: NOTHING WAS EVER PROMISED (no pair -- the flag is off, or the 202
   * carried none), where today's confirmation is the honest screen and silence
   * is deliberate; and A PROMISE WAS MADE AND CANNOT BE KEPT, where the visitor
   * has already read "Open it on any device. We'll sign you in here." and needs
   * to be told it no longer holds. One name for both is what let
   * `continuationLines`' empty arm look correct: a real walk (laptop requests,
   * phone opens, phone signs in) left the laptop promising a cross-device
   * sign-in for the full 30-minute TTL and then saying "That link has expired."
   * about a link that had worked half an hour earlier.
   *
   * Deliberately NOT terminal. The link in the inbox is still good -- only this
   * device's hand-off is gone -- so "Check your email" stays true, the resend
   * keeps its own words, and the state adds one line rather than taking the
   * screen over.
   */
  | { kind: 'handoffUnavailable' }
  | { kind: 'waiting' }
  | { kind: 'approved'; flowId: string; requestSecret: string }
  | { kind: 'claiming'; flowId: string }
  | { kind: 'claimed' }
  | { kind: 'taken' }
  | { kind: 'denied' }
  | { kind: 'expired' }
  | { kind: 'claimFailed' }
  /** Another tab in THIS browser is spending the token. */
  | { kind: 'claimedElsewhere' }
  /**
   * The claim SUCCEEDED, the redemption was started, and the document is still
   * here some seconds later.
   *
   * Produced only by the door's failure reveal, and it is deliberately not
   * `claimFailed`: nothing has failed that we know of. `onSignInSettled` fires
   * on BOTH outcomes, and on web a SUCCESS settles it too -- just before the
   * browser leaves -- so a reveal is a hedge on TIMING and never a verdict on
   * outcome. `AuthLinkLanding`'s own reveal makes the same distinction and its
   * `revealSignIn` note has the prod measurement behind it.
   */
  | { kind: 'signInStalled' };

/**
 * The states where the door has said its LAST WORD about this flow: no poll
 * and no claim of its own is still to come, so the screen owes a heading and
 * exactly one working exit. (The TTL tick is a separate list and still runs
 * under most of these -- see the note below.)
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: the criterion is "this screen
 * has stopped acting", not "this document can never complete the flow", and
 * the difference is not pedantic -- `signInStalled` is a member while its own
 * doc comment says the redirect may still land here. What makes a state a
 * member is narrow, and narrower than this note once claimed: the door will
 * issue no further poll and no further claim FOR THIS FLOW, and has no action
 * of its own left to offer, so the screen owes a heading and one exit.
 *
 * Three things that does NOT mean, each of them a claim this note carried
 * until it was checked against the head:
 *   - The TTL tick is not disarmed. `isTtlTickArmed` in `AuthDoor.tsx` excludes
 *     only `expired`, `unavailable`, `handoffUnavailable`, `claiming` and
 *     `signInStalled`, so it is ARMED under `claimed`, `taken`, `denied`,
 *     `claimFailed` and `claimedElsewhere`.
 *   - So the copy can still change. The green row `an override never masks
 *     expired once the newest record is past the TTL` shows `taken` becoming
 *     `expired` on that tick, which disproves both of the above at once.
 *   - The record is not necessarily forgotten. A `claimed` (or `claiming`)
 *     record is KEPT until `hasLogtoSession`, because a token that was minted
 *     and stored is only safe to drop once a session proves it was spent.
 *
 * Reading the set as "the flow cannot complete here" also makes the five
 * non-members look arbitrary, when three are excluded for one reason --
 * something is still due. `waiting` and `approved` are waiting on the poll, and
 * `claiming` is a page in the middle of unloading with a redirect behind it.
 * `unavailable` is the fourth and its reason is not "retried on the next tick":
 * `useContinuationStatus`'s `refetchInterval` returns `false` for exactly the
 * reads that produce it permanently -- `invalid`, 400 and 404 -- and the row
 * `a 404 sets isUnavailable, keeps the last ok statuses, and stops` pins that.
 * What excludes it is that it is the ABSENCE of news rather than the door's
 * last word about the flow: the state reports a read that failed, the last ok
 * statuses are retained under it, and on every other cause the poll backs off
 * and comes back.
 *
 * `handoffUnavailable` is the fifth, and it is the one non-member that is
 * neither "something is still due" nor "absence of news": the door HAS said its
 * last word about the hand-off. It is excluded because the criterion's second
 * clause fails and not its first -- the link in the inbox is still good, so the
 * door still has an action of its own to offer (the resend keeps its own words
 * under it) and the state adds one line rather than taking the screen over. Do
 * not "complete" the set by adding it; its own doc comment on the union says
 * why, and `AUTH_DOOR_COPY` has no terminal heading for it.
 *
 * It lives beside the union and not in `AuthDoor.tsx` because it is a property
 * OF the union -- adding a member is what should force the question -- and
 * because the door reads it in TWO places, both off one `isTerminalState`
 * derivation: the copy block and the primary control. It is not what arms the
 * TTL tick; that has its own exclusion list, which overlaps this set without
 * matching it. Group D of PR #1143 round 1: five states were
 * appending their sentence under an unconditional "Check your email" and
 * falling through to a resend button the send cooldown left inert for 60s,
 * so `denied` read as "check your email ... that sign-in was cancelled" with
 * no control that did anything.
 *
 */
const TERMINAL_DOOR_KINDS: readonly DoorState['kind'][] = [
  'claimed',
  'taken',
  'denied',
  'expired',
  'claimFailed',
  'signInStalled',
  // Merged in with the provider. The sibling has spent the token, so this door
  // has nothing left to poll for and nothing left to claim: it is an outcome
  // and owes a heading and one working exit like every other one. Its copy is
  // written to what is known at the broadcast instant -- the claim, not the
  // sign-in -- precisely because membership here promotes it to the heading;
  // see `AUTH_DOOR_COPY.claimedElsewhere`. That exit is the terminal one with
  // one difference, because the sibling may still be redeeming: its send never
  // forgets the sibling-claimed record (`sendLink`). (It used to keep the
  // auto-redemption cue too; no send touches the cue any more.)
  'claimedElsewhere',
];

export const isTerminalDoorState = (state: DoorState): boolean =>
  TERMINAL_DOOR_KINDS.includes(state.kind);

/**
 * The door's own verdict about one flow, which outranks whatever the poll says
 * next. Keyed by `flowId` so a stale override cannot survive into a new flow,
 * and held one per flow (`DoorOverrides`) so it cannot erase another flow's.
 *
 * EVERY PRODUCER, counted at the `withOverride` calls in `AuthDoor.tsx`:
 *  - `claiming`: `leave()`, as the redemption starts.
 *  - `taken` and `denied`: the claim's 409, by its `detail` (webapp #1188).
 *    `address_denied` sets `denied` (somebody pressed "This wasn't me" on
 *    another link for this address, so the claim will never be granted), and
 *    every other 409 sets `taken`. A cancel drops either one, and
 *    `reapplyOwnSpentClaim` puts it back from the claim's recorded answer
 *    (#1248).
 *  - `claimFailed`: the claim's 410 or 404, and its retries running out.
 *  - `claimed`: `settleBehindTheHandOff`, for a claim of this tab's own that
 *    settles ok while another flow holds the door's one hand-off; and
 *    `reapplyClaimedElsewhere`, for a flow the poll can no longer judge.
 *    Three callers reach that one: `putFlowBackUnderThePoll` (from
 *    `putPairBack`, which the throttled re-seed calls and so does a detached
 *    send's 202 that a Continue took back, and from a failed send's restore in
 *    `onError` and
 *    `settleAbandonedSend`), `putPairBack`'s stored-marker branch, and
 *    `reapplyOwnSpentClaim` (from `putFlowBackUnderThePoll` and from
 *    `sendLink`'s ordinary throttled repeat).
 *  - `claimedElsewhere`: the cross-tab subscriber, and
 *    `reapplyClaimedElsewhere` putting it back on a flow the memory learned
 *    from a broadcast.
 *  - `signInStalled`: the redemption's settle, `SIGN_IN_FAILURE_REVEAL_MS`
 *    later.
 *
 * THREE members can arrive from two directions: `taken`, `denied` and
 * `claimed` are the status poll's own verdicts too (`deriveDoorState`). The
 * door sets them where its own answer is the one to show (a claim's 409, a
 * claim of its own behind the hand-off) or where the poll can no longer say
 * them (a silenced flow, after a cancel). The router-state entry that could
 * also set `denied` never had a producer of its own (the provider forgets a
 * refused record rather than navigating with it) and was dropped with #1161.
 * `claimedElsewhere` arrives the other way a poll cannot reach at all: a
 * sibling tab's cross-tab message. Every other member is the door's alone.
 */
export interface DoorOverride {
  flowId: string;
  state: Extract<
    DoorState,
    {
      kind:
        | 'claiming'
        | 'claimed'
        | 'taken'
        | 'claimFailed'
        | 'denied'
        | 'claimedElsewhere'
        | 'signInStalled';
    }
  >;
}

/**
 * Every override the door holds, ONE PER FLOW, keyed by `flowId`.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: this was a single slot, and
 * two of its writers are about different flows at once: the claim's verdict is
 * about the cross-record `approved` winner, and a sibling tab's broadcast is
 * about whichever flow that tab spent. A message about an OLDER flow replaced
 * the verdict about the approved one, the render (which picks by the described
 * flow) missed, and the screen fell back to the raw derivation with the poll
 * already stopped: after a 410, "Approved. Signing you in." with nothing in
 * flight. A per-flow map means a verdict can only be replaced by another
 * verdict about the same flow, and a new attempt clears every entry except a
 * sibling's claim, which describes the flow rather than the attempt and is
 * the one verdict the poll can never re-learn.
 */
export type DoorOverrides = Readonly<Record<string, DoorOverride['state']>>;

/** `overrides` with `override` set for its flow, and every other flow's left alone. */
export const withOverride = (
  overrides: DoorOverrides,
  { flowId, state }: DoorOverride,
): DoorOverrides => ({ ...overrides, [flowId]: state });

/**
 * Turns records + the last known statuses into one door state.
 *
 * Pure, and takes `now` rather than reading the clock, so the door's render and
 * its tests see the same answer for the same inputs.
 *
 * Precedence: no records at all is `unavailable` (a 202 without a pair, or the
 * flag off) and not `expired`, because nothing ever expired. Everything else is
 * scoped to the NEWEST live record — it is the one the visitor just asked for
 * and the one the screen is describing — EXCEPT `approved`, which wins across
 * records, so a visitor who requested a second link and then approved the first
 * still gets signed in. An `approved` on a record past the TTL is not an exit:
 * the token behind it is gone too.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: a NEWEST `denied` outranks
 * that cross-record `approved`, and nothing else does (#1163, Option 3,
 * decided 2026-09-29). A deny ends the whole wait: the door says
 * the newest link's verdict, never mentions an older one, and stops waiting on
 * every record it holds (the door's marking effect). Without this rule the
 * same answer carrying a newest `denied` and an older `approved` derived
 * `approved`, and the door claimed and signed the visitor in from under "That
 * sign-in was cancelled." Checked here and not in the door's claim effect so
 * that nothing can be claimed after a deny, even inside one answer. Backend
 * #892's R2-4c refuses the older approval too (`address_denied`), modulo that
 * review's case-folding residual; this keeps the door from asking at all. A
 * newest `taken`, `claimed` or `expired` still yields to an older approval:
 * none of them is the visitor saying no.
 */
export const deriveDoorState = (
  records: ContinuationRecord[],
  statuses: Record<string, ContinuationStatus> | undefined,
  now: number,
): DoorState => {
  if (records.length === 0) {
    return { kind: 'unavailable' };
  }

  const live = liveContinuations(records, now);
  if (live.length === 0) {
    return { kind: 'expired' };
  }

  const newestStatus = statuses?.[live[0].flowId];
  if (newestStatus === 'denied') {
    return { kind: 'denied' };
  }

  const approved = live.find(candidate => statuses?.[candidate.flowId] === 'approved');
  if (approved) {
    return {
      kind: 'approved',
      flowId: approved.flowId,
      requestSecret: approved.requestSecret,
    };
  }

  switch (newestStatus) {
    case 'claimed':
      return { kind: 'claimed' };
    case 'taken':
      return { kind: 'taken' };
    case 'expired':
      return { kind: 'expired' };
    default:
      // 'pending', an id the response did not answer for, or no statuses yet.
      return { kind: 'waiting' };
  }
};

/**
 * Fire-and-forget over the record store.
 *
 * The store's own adapters swallow their storage exceptions, the read as well
 * as the write: `readMagicLinkMirror` and `writeMagicLinkMirror` catch a
 * browser that throws on the `localStorage` property access itself (Safari,
 * "Block All Cookies"), and the native adapter catches the bridge. So no call
 * below rejects today, and a refused write RESOLVES like a landed one. This is
 * the belt for a store that ever does reject: none of the calls has a recovery
 * -- the pair lives in the caller's own state for the life of the page either
 * way -- so the failure would be dropped rather than left as an unhandled
 * rejection.
 *
 * Module scope so it is referentially stable: every caller is inside an effect
 * or a callback, and a component-scope helper would have to ride in each of
 * their dependency arrays. Shared between `AuthDoor` and
 * `MagicLinkContinuationProvider` rather than duplicated in each.
 */
export const detach = (work: Promise<unknown>): void => {
  void work.catch(() => undefined);
};
